'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import { AttachmentThumbnail } from './attachment-thumbnail';

export function ReportDetails({
  report,
}: {
  report: { id: string; description: string; screenshots: string[] };
}) {
  const { t } = useTranslation('common');

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h5 className="text-sm font-semibold">
          {t('bugReport.fields.description.label', {
            defaultValue: 'Description',
          })}
        </h5>
        <p
          dir="auto"
          data-test="report-full-description"
          className="text-base leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]"
        >
          {report.description}
        </p>
      </section>
      {report.screenshots.length > 0 ? (
        <section className="space-y-3 border-t pt-5">
          <h5 className="text-sm font-semibold">
            {t('bugReport.myReports.attachments', {
              defaultValue: 'Attachments ({{count}})',
              count: report.screenshots.length,
            })}
          </h5>
          <ReportAttachments reportId={report.id} paths={report.screenshots} />
        </section>
      ) : null}
    </div>
  );
}

export function ReportAttachments({
  reportId,
  paths,
  signingPaths = paths,
}: {
  reportId: string;
  paths: string[];
  signingPaths?: string[];
}) {
  const { t } = useTranslation('common');
  const supabase = useSupabase();
  const { data: signedUrls, isLoading } = useQuery({
    queryKey: ['bug-report-attachments', reportId, signingPaths.join(',')],
    enabled: signingPaths.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from('bug_reports')
        .createSignedUrls(signingPaths, 60 * 60);

      if (error) throw error;
      return new Map((data ?? []).map((file) => [file.path, file.signedUrl]));
    },
  });

  return (
    <ul className="flex flex-wrap gap-3">
      {paths.map((path, index) => {
        const label = t('bugReport.myReports.attachment', {
          defaultValue: 'Attachment {{number}}',
          number: index + 1,
        });
        const signedUrl = signedUrls?.get(path);

        return (
          <li key={path} className="min-w-0 max-w-full space-y-1">
            {signedUrl ? (
              <AttachmentThumbnail
                path={path}
                signedUrl={signedUrl}
                label={label}
              />
            ) : (
              <p className="text-muted-foreground text-sm" role="status">
                {isLoading
                  ? t('bugReport.myReports.attachmentLoading', {
                      defaultValue: 'Loading attachment…',
                    })
                  : t('bugReport.myReports.attachmentUnavailable', {
                      defaultValue: 'Attachment link unavailable',
                    })}
              </p>
            )}
            <p className="text-muted-foreground text-xs">{label}</p>
          </li>
        );
      })}
    </ul>
  );
}
